import { ReactNode } from 'react';
import { Box, Group, ActionIcon, Popover, ScrollArea } from '@mantine/core';
import { IconQuestionMark, IconMenu2, IconX } from '@tabler/icons-react';
import { useChatStore } from '../../../stores/chatStore';
import { useStreamingStore } from '../../../stores/streamingStore';
import { ProfileModal } from '../../../features/chat/components/ProfileModal';
import { trackComponentInteraction } from '../../../utils/analytics';
import { ConversationTitle } from './ConversationTitle';
import { SCROLL_EFFECTS_CONFIG } from '../../../config/scrollEffects';
import { useScrollFadeEffect } from '../../../hooks/useScrollFadeEffect';
import { useGenieAccess } from '../../../hooks/useGenieAccess';
import classes from './ChatLayout.module.css';
import { useConversationStore } from '../../../stores/conversationStore';
import { useShallow } from 'zustand/react/shallow';
import { useEffect } from 'react';

interface ChatLayoutProps {
  children: ReactNode;
  className?: string;
  headerAction?: ReactNode;
  footerContent?: ReactNode;
  showHeaderLeft?: boolean;
  showThoughtProcess?: boolean;
  thoughtProcessPanel?: ReactNode;
  /** Override default scroll configuration */
  scrollConfig?: Partial<typeof SCROLL_EFFECTS_CONFIG>;
  /** Ref for the scroll area to enable auto-scroll functionality */
  scrollAreaRef?: React.RefObject<HTMLDivElement>;
  /** Ref for the scroll area viewport to enable auto-scroll functionality */
  viewportRef?: React.RefObject<HTMLDivElement>;
}

export const ChatLayout = ({
  children,
  className,
  headerAction,
  footerContent,
  showHeaderLeft = true,
  showThoughtProcess = false,
  thoughtProcessPanel,
  scrollConfig,
  scrollAreaRef,
  viewportRef
}: ChatLayoutProps) => {
  const { isProfileModalOpen, openProfileModal, closeProfileModal, toggleProfileModal } = useChatStore();
  const connectionStatus = useStreamingStore((s) => s.connectionStatus);
  const isStreaming = useStreamingStore((s) => s.isStreaming);
  const showConnectionBanner = connectionStatus === 'reconnecting' || (connectionStatus === 'disconnected' && isStreaming);

  // Initialize Genie access
  useGenieAccess();

  // Use custom hook for scroll fade effects with configuration override
  const { isScrolling, handleScrollPositionChange } = useScrollFadeEffect(scrollConfig);


  // Add this useEffect after line 45
useEffect(() => {
  if (viewportRef?.current) {
    const viewport = viewportRef.current;
    viewport.style.overscrollBehavior = 'none';
    viewport.style.overscrollBehaviorY = 'none';
  }
}, [viewportRef]);


  // Determine content wrapper class based on thought process panel visibility
  const contentWrapperClass = showThoughtProcess
    ? `${classes.contentWrapper} ${classes.contentWrapperWithPanel}`
    : classes.contentWrapper;

  return (
    <Box className={`${classes.container} ${className || ''}`}>
      <Box className={contentWrapperClass}>
        {/* Main Section - contains header, content, footer */}
        <Box className={classes.mainSection}>
          {/* Header */}
          <Group
            justify={showHeaderLeft ? 'space-between' : 'flex-start'}
            className={classes.header}
            wrap="nowrap"
          >
            {showHeaderLeft && (
              <Group gap="sm" wrap="nowrap" className={classes.headerLeft}>
                <MobileNavbarToggle />
                <ConversationTitle />
              </Group>
            )}
            {headerAction || (
              <Popover
                width={240}
                position="bottom-end"
                offset={0}
                opened={isProfileModalOpen}
                onChange={closeProfileModal}
                styles={{
                  dropdown: {
                    border: '1px solid var(--fonterra-border-accent)',
                    borderRadius: '6px',
                    backgroundColor: 'var(--fonterra-surface-popover)',
                    padding: 0,
                    boxShadow: 'var(--fonterra-shadow-popover)',
                  }
                }}
              >
                <Popover.Target>
                  <ActionIcon
                    size={42}
                    variant="filled"
                    classNames={{ root: classes.profileButton }}
                    onClick={() => {
                      trackComponentInteraction(
                        'Header',
                        'Profile',
                        'button',
                        'click',
                        'profile_access'
                      );
                      toggleProfileModal();
                    }}
                  >
                    <IconQuestionMark size={24} />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown>
                  <ProfileModal onClose={closeProfileModal} />
                </Popover.Dropdown>
              </Popover>
            )}
          </Group>

          {/* Connection status banner */}
          {showConnectionBanner && (
            <Box style={{
              padding: '6px 16px',
              backgroundColor: connectionStatus === 'reconnecting' ? '#fff3cd' : '#f8d7da',
              color: connectionStatus === 'reconnecting' ? '#664d03' : '#842029',
              fontSize: '13px',
              textAlign: 'center',
            }}>
              {connectionStatus === 'reconnecting' ? 'Reconnecting...' : 'Connection lost. Please refresh the page.'}
            </Box>
          )}

          {/* Content Area */}
          <ScrollArea
            ref={scrollAreaRef}
            viewportRef={viewportRef}

            className={`${classes.contentArea} ${isScrolling ? classes.scrolling : ''}`}
            classNames={{ viewport: classes.scrollViewport }}
            scrollbars="y"
            type="always"
            offsetScrollbars
            onScrollPositionChange={handleScrollPositionChange}
            styles={{
              viewport: {
                paddingRight: '0px',
                paddingBottom: 'var(--mantine-spacing-lg)',
              }
            }}
          >
            {children}
          </ScrollArea>

          {/* Footer */}
          <Box className={classes.footer}>
            {footerContent}
          </Box>
        </Box>

        {/* Vertical Divider */}
        {showThoughtProcess && (
          <Box className={classes.divider} />
        )}

        {/* Thought Process Panel - spans full height */}
        {showThoughtProcess && thoughtProcessPanel && (
          <Box className={classes.thoughtProcessPanel}>
            {thoughtProcessPanel}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export type { ChatLayoutProps };

// Local component: Mobile navbar burger toggle
const MobileNavbarToggle = () => {
  const { isMobileNavbarOpen, toggleMobileNavbar } = useConversationStore(
    useShallow((s) => ({
      isMobileNavbarOpen: s.isMobileNavbarOpen,
      toggleMobileNavbar: s.toggleMobileNavbar,
    }))
  );

  return (
    <ActionIcon
      size={42}
      radius={6}
      variant="subtle"
      onClick={toggleMobileNavbar}
      aria-label={isMobileNavbarOpen ? 'Close navigation' : 'Open navigation'}
      hiddenFrom="sm"
      className={classes.mobileMenu}
    >
      {isMobileNavbarOpen ? (
        <IconX size={24} />
      ) : (
        <IconMenu2 size={24} />
      )}
    </ActionIcon>
  );
};
